import React, { useState, useEffect } from 'react';
import apiClient from '../config/axios';
import { useWorkspace } from '../contexts/WorkspaceContext';

/**
 * Phase 3: Analytics Dashboard Component
 * Real-time lead monitoring and performance metrics
 */

const AnalyticsDashboard = () => {
  const { currentWorkspace } = useWorkspace();
  const [timeRange, setTimeRange] = useState('7');
  const [dashboardData, setDashboardData] = useState(null);
  const [leadTrends, setLeadTrends] = useState([]);
  const [intentDistribution, setIntentDistribution] = useState([]);
  const [leadFunnel, setLeadFunnel] = useState(null);
  const [recentLeads, setRecentLeads] = useState([]);
  const [activeConversations, setActiveConversations] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!currentWorkspace?.id) {
      setLoading(false);
      return;
    }
    fetchAnalytics();
    // Refresh every 30 seconds
    const interval = setInterval(fetchAnalytics, 30000);
    return () => clearInterval(interval);
  }, [timeRange, currentWorkspace?.id]);

  const fetchAnalytics = async () => {
    if (!currentWorkspace?.id) return;
    try {
      const headers = { 'x-workspace-id': currentWorkspace.id };

      // Fetch all analytics data in parallel
      const [dashboard, trends, intents, funnel, leads, conversations] = await Promise.all([
        apiClient.get(`/api/analytics/dashboard?range=${timeRange}`, { headers }),
        apiClient.get(`/api/analytics/lead-trends?days=${timeRange}`, { headers }),
        apiClient.get(`/api/analytics/intent-distribution?days=${timeRange}`, { headers }),
        apiClient.get(`/api/analytics/lead-funnel?days=${timeRange}`, { headers }),
        apiClient.get(`/api/analytics/recent-leads?limit=10`, { headers }),
        apiClient.get(`/api/analytics/active-conversations`, { headers })
      ]);

      setDashboardData(dashboard.data.data);
      setLeadTrends(trends.data.data);
      setIntentDistribution(intents.data.data);
      setLeadFunnel(funnel.data.data);
      setRecentLeads(leads.data.data);
      setActiveConversations(conversations.data.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching analytics:', error);
      setLoading(false);
    }
  };

  const exportLeads = async () => {
    if (!currentWorkspace?.id) return;
    try {
      const response = await apiClient.get('/api/analytics/export-leads?filter=all', {
        headers: { 'x-workspace-id': currentWorkspace.id },
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `leads-export-${Date.now()}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error exporting leads:', error);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-xl">Loading analytics...</div>
      </div>
    );
  }


  if (!currentWorkspace?.id) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-xl text-gray-600">Select a workspace to view analytics.</div>
      </div>
    );
  }

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      {/* Header */}
      <div className="mb-6 flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">📊 Leads Analysis</h1>
          <p className="text-gray-600 mt-1">AI-powered lead capture insights</p>
        </div>
        <div className="flex gap-3">
          <select
            value={timeRange}
            onChange={(e) => setTimeRange(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
          >
            <option value="7">Last 7 days</option>
            <option value="14">Last 14 days</option>
            <option value="30">Last 30 days</option>
            <option value="90">Last 90 days</option>
          </select>
          <button
            onClick={exportLeads}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
          >
            📥 Export CSV
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
        <StatCard
          icon="💬"
          title="Total Conversations"
          value={dashboardData?.totalConversations || 0}
          subtitle={`${dashboardData?.activeConversations || 0} active now`}
          color="blue"
        />
        <StatCard
          icon="💎"
          title="Total Leads"
          value={dashboardData?.totalLeads || 0}
          subtitle={`${dashboardData?.captureRate || 0}% capture rate`}
          color="purple"
        />
        <StatCard
          icon="✅"
          title="Qualified Leads"
          value={dashboardData?.qualifiedLeads || 0}
          subtitle={`${dashboardData?.qualificationRate || 0}% qualification rate`}
          color="green"
        />
        <StatCard
          icon="⭐"
          title="Avg Lead Score"
          value={`${dashboardData?.averageScore || 0}/100`}
          subtitle="Quality metric"
          color="yellow"
        />
      </div>

      {/* Lead Funnel */}
      {leadFunnel && (
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <h2 className="text-xl font-bold mb-4">🎯 Conversion Funnel</h2>
          <div className="space-y-3">
            {leadFunnel.stages.map((stage, index) => (
              <FunnelStage key={index} stage={stage} total={leadFunnel.stages[0].count} />
            ))}
          </div>
        </div>
      )}

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        {/* Lead Trends */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-xl font-bold mb-4">📈 Lead Trends</h2>
          <div className="space-y-2">
            {leadTrends.slice(-10).map((trend, index) => (
              <div key={index} className="flex justify-between items-center p-2 hover:bg-gray-50 rounded">
                <span className="text-sm text-gray-600">
                  {new Date(trend.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                </span>
                <div className="flex gap-4">
                  <span className="text-sm font-medium">{trend.totalLeads} leads</span>
                  <span className="text-sm text-green-600">{trend.qualifiedLeads} qualified</span>
                  <span className="text-sm text-blue-600">{trend.avgScore} avg</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Intent Distribution */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-xl font-bold mb-4">🎯 Intent Distribution</h2>
          <div className="space-y-3">
            {intentDistribution.map((intent, index) => (
              <IntentBar key={index} intent={intent} total={intentDistribution[0]?.count || 1} />
            ))}
          </div>
        </div>
      </div>

      {/* Active Conversations */}
      {activeConversations.length > 0 && (
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-bold">💬 Active Conversations</h2>
            <span className="px-3 py-1 bg-green-100 text-green-800 rounded-full text-sm font-medium">
              {activeConversations.length} active
            </span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-2 text-left text-sm font-medium text-gray-700">Contact</th>
                  <th className="px-4 py-2 text-left text-sm font-medium text-gray-700">State</th>
                  <th className="px-4 py-2 text-left text-sm font-medium text-gray-700">Intent</th>
                  <th className="px-4 py-2 text-left text-sm font-medium text-gray-700">Score</th>
                  <th className="px-4 py-2 text-left text-sm font-medium text-gray-700">Idle</th>
                </tr>
              </thead>
              <tbody>
                {activeConversations.map((conv) => (
                  <tr key={conv.id} className="border-t hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <div>
                        <div className="font-medium">{conv.contactName}</div>
                        <div className="text-sm text-gray-500">{conv.phone}</div>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded text-xs">
                        {conv.currentState}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm">{conv.intent || 'unknown'}</td>
                    <td className="px-4 py-3">
                      <span className={`font-medium ${conv.leadScore >= 80 ? 'text-green-600' : conv.leadScore >= 60 ? 'text-blue-600' : 'text-gray-600'}`}>
                        {conv.leadScore || 'N/A'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">{conv.minutesIdle}m ago</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Recent Leads */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <h2 className="text-xl font-bold mb-4">🆕 Recent Leads</h2>
        <div className="space-y-4">
          {recentLeads.map((lead) => (
            <LeadCard key={lead.id} lead={lead} />
          ))}
        </div>
      </div>
    </div>
  );
};

// Stat Card Component
const StatCard = ({ icon, title, value, subtitle, color }) => {
  const colorClasses = {
    blue: 'bg-blue-50 border-blue-200',
    purple: 'bg-purple-50 border-purple-200',
    green: 'bg-green-50 border-green-200',
    yellow: 'bg-yellow-50 border-yellow-200'
  };

  return (
    <div className={`${colorClasses[color]} border-2 rounded-lg p-5 transition hover:shadow-md`}>
      <div className="flex items-start justify-between">
        <div>
          <p className="text-sm text-gray-600 mb-1">{title}</p>
          <p className="text-3xl font-bold">{value}</p>
          <p className="text-sm text-gray-500 mt-1">{subtitle}</p>
        </div>
        <span className="text-3xl">{icon}</span>
      </div>
    </div>
  );
};

// Funnel Stage Component
const FunnelStage = ({ stage, total }) => {
  const width = (stage.count / total) * 100;

  return (
    <div>
      <div className="flex justify-between items-center mb-1">
        <span className="text-sm font-medium">{stage.name}</span>
        <span className="text-sm text-gray-600">
          {stage.count} ({stage.percentage}%)
        </span>
      </div>
      <div className="w-full bg-gray-200 rounded-full h-3">
        <div
          className="bg-gradient-to-r from-blue-500 to-purple-600 h-3 rounded-full transition-all duration-500"
          style={{ width: `${width}%` }}
        />
      </div>
    </div>
  );
};

// Intent Bar Component
const IntentBar = ({ intent, total }) => {
  const width = Math.min((intent.count / total) * 100, 100);

  const intentColors = {
    inquiry: 'bg-blue-500',
    purchase: 'bg-green-500',
    demo: 'bg-purple-500',
    pricing: 'bg-yellow-500',
    support: 'bg-red-500',
    general: 'bg-gray-500'
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-1">
        <span className="text-sm font-medium capitalize">{intent.intent}</span>
        <span className="text-sm text-gray-600">{intent.count} ({intent.avgConfidence})</span>
      </div>
      <div className="w-full bg-gray-200 rounded-full h-2">
        <div
          className={`${intentColors[intent.intent] || 'bg-gray-500'} h-2 rounded-full transition-all duration-500`}
          style={{ width: `${width}%` }}
        />
      </div>
    </div>
  );
};

// Lead Card Component
const LeadCard = ({ lead }) => {
  const scoreColor = lead.leadScore >= 80 ? 'text-green-600' : lead.leadScore >= 60 ? 'text-blue-600' : 'text-gray-600';

  return (
    <div className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition">
      <div className="flex justify-between items-start mb-2">
        <div>
          <h3 className="font-bold text-lg">{lead.fullName || 'Unknown'}</h3>
          <p className="text-sm text-gray-600">{lead.companyName || 'No company'}</p>
        </div>
        <div className="flex gap-2">
          {lead.qualified && (
            <span className="px-2 py-1 bg-green-100 text-green-800 rounded text-xs font-medium">
              ✓ Qualified
            </span>
          )}
          <span className={`px-2 py-1 bg-gray-100 rounded text-xs font-medium ${scoreColor}`}>
            {lead.leadScore}/100
          </span>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-2 text-sm">
        {lead.email && (
          <div className="flex items-center gap-1">
            <span>📧</span>
            <span className="text-gray-700">{lead.email}</span>
          </div>
        )}
        {lead.phone && (
          <div className="flex items-center gap-1">
            <span>📱</span>
            <span className="text-gray-700">{lead.phone}</span>
          </div>
        )}
      </div>
      {lead.requirements && (
        <p className="text-sm text-gray-600 mt-2 line-clamp-2">
          <strong>Requirements:</strong> {lead.requirements}
        </p>
      )}
      <div className="flex justify-between items-center mt-3 pt-3 border-t">
        <span className="text-xs text-gray-500">
          {new Date(lead.createdAt).toLocaleString()}
        </span>
        {lead.intent && (
          <span className="text-xs px-2 py-1 bg-blue-50 text-blue-700 rounded">
            {lead.intent}
          </span>
        )}
      </div>
    </div>
  );
};

export default AnalyticsDashboard;
